<template>
  <div class="min-h-screen bg-gray-50">
    <Header />
    <main class="p-6">
      <div class="max-w-7xl mx-auto">
        <h1 class="text-3xl font-bold text-gray-900 mb-6">Analytics</h1>
        
        <!-- Error Message -->
        <ErrorMessage 
          v-if="error" 
          :message="error" 
          title="Error Loading Analytics"
        />

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-8">
          <div class="spinner mx-auto"></div>
          <p class="text-gray-600 mt-2">Loading analytics...</p>
        </div>

        <!-- Analytics Content -->
        <div v-else-if="analytics" class="space-y-8">
          <!-- Dashboard Cards -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div class="card">
              <div class="card-body text-center">
                <h3 class="text-lg font-semibold text-gray-900">Total Clicks</h3>
                <p class="text-3xl font-bold text-blue-600">{{ formatNumber(analytics.totalClicks) }}</p>
                <p class="text-xs text-gray-500 mt-1">*Approximate for large datasets</p>
              </div>
            </div>
            
            <div class="card">
              <div class="card-body text-center">
                <h3 class="text-lg font-semibold text-gray-900">Total Links</h3>
                <p class="text-3xl font-bold text-green-600">{{ formatNumber(analytics.totalLinks) }}</p>
                <p class="text-xs text-gray-500 mt-1">*Approximate for large datasets</p>
              </div>
            </div>
            
            <div class="card">
              <div class="card-body text-center">
                <h3 class="text-lg font-semibold text-gray-900">Active Tenants</h3>
                <p class="text-3xl font-bold text-purple-600">{{ analytics.tenantSummary.length }}</p>
              </div>
            </div>
          </div>

          <!-- Top Links -->
          <div class="card mb-16">
            <div class="card-header">
              <h2 class="text-xl font-semibold text-gray-900">Top Links by Clicks</h2>
            </div>
            <div class="card-body p-0">
              <table class="table">
                <thead>
                  <tr>
                    <th>Short Code</th>
                    <th>Title</th>
                    <th>Tenant</th>
                    <th>Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="link in analytics.topLinks" :key="link.shortCode">
                    <td>
                      <span class="shortcode-outline">
                        {{ link.shortCode }}
                      </span>
                    </td>
                    <td>{{ link.title || 'Untitled' }}</td>
                    <td>{{ link.tenantName || 'Unknown' }}</td>
                    <td>
                      <span class="font-semibold">{{ formatNumber(link.clickCount) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Tenant Summary -->
          <div class="card">
            <div class="card-header">
              <h2 class="text-xl font-semibold text-gray-900">Tenant Summary</h2>
            </div>
            <div class="card-body p-0">
              <table class="table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Links</th>
                    <th>Total Clicks</th>
                    <th>Avg Clicks per Link</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tenant in analytics.tenantSummary" :key="tenant.tenantId">
                    <td>{{ tenant.tenantName }}</td>
                    <td>{{ formatNumber(tenant.linkCount) }}</td>
                    <td>{{ formatNumber(tenant.totalClicks) }}</td>
                    <td>
                      <span class="font-semibold">
                        {{ tenant.linkCount > 0 ? formatNumber(Math.round(tenant.totalClicks / tenant.linkCount)) : '0' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import Header from '@/components/common/Header.vue'
import ErrorMessage from '@/components/common/ErrorMessage.vue'
import { adminService } from '@/services/admin'

const analytics = ref<{
  totalClicks: number;
  totalLinks: number;
  topLinks: Array<{
    shortCode: string;
    title?: string;
    clickCount: number;
    tenantId: string;
    tenantName?: string;
  }>;
  tenantSummary: Array<{
    tenantId: string;
    tenantName: string;
    linkCount: number;
    totalClicks: number;
  }>;
} | null>(null)

const isLoading = ref(false)
const error = ref('')

const loadAnalytics = async () => {
  try {
    isLoading.value = true
    error.value = ''
    analytics.value = await adminService.getAnalyticsOverview()
  } catch (e: any) {
    error.value = e?.message || 'Failed to load analytics'
  } finally {
    isLoading.value = false
  }
}

const formatNumber = (num: number): string => {
  return new Intl.NumberFormat().format(num)
}

onMounted(loadAnalytics)
</script>
